// ─── Use Cases Registry ─────────────────────────────────────
// Centralized data for all use case pages

export type DMMessage = {
  from: "user" | "bot"
  text: string
  hasLink?: { label: string; url: string }
}

export type UseCaseData = {
  slug: string
  name: string
  badge: string
  description: string
  icon: "MessageSquare" | "Link" | "Mail" | "Smartphone"
  color: {
    text: string
    bg: string
    border: string
  }
  seo: {
    title: string
    description: string
    keywords: string
  }
  hero: {
    h1: string
    subtitle: string
  }
  phonePreview: {
    username: string
    profilePic: string
    messages: DMMessage[]
  }
  problem: {
    headline: string
    description: string
    painPoints: { title: string; description: string }[]
  }
  steps: {
    headline: string
    items: { title: string; description: string }[]
  }
  flows: {
    headline: string
    subtitle: string
    items: { icon: "Video" | "Image" | "Gift" | "Tag" | "ShoppingBag" | "Smartphone" | "BookOpen" | "MessageCircle" | "Key" | "Users" | "Mail" | "UserPlus" | "Flame" | "BarChart3"; title: string; trigger: string; response: string }[]
  }
  faqs: { question: string; answer: string }[]
  relatedSlugs: string[]
  overview: {
    headline: string
    body: string
    highlights: { icon: string; title: string; text: string }[]
    assetSlug: string
    hasGif: boolean
  }
  proofPoints: {
    headline: string
    items: { metric: string; label: string; description: string }[]
  }
  workflowGuide: {
    headline: string
    subtitle: string
    steps: { title: string; body: string }[]
  }
  testimonial: {
    quote: string
    name: string
    handle: string
    role: string
  }
}

export const useCases: UseCaseData[] = [
  // ─── 1. Auto-Respond to Comments ─────────────────────────
  {
    slug: "instagram-comment-dm-automation",
    name: "Auto-Respond to Comments",
    badge: "Comment to DM",
    description: "When followers comment on your posts or Reels, DM them your link instantly. No manual work.",
    icon: "MessageSquare",
    color: {
      text: "text-blue-600 dark:text-blue-400",
      bg: "bg-blue-50 dark:bg-blue-950/40",
      border: "border-blue-200 dark:border-blue-800/50",
    },
    seo: {
      title: "Auto-Reply to Instagram Comments with AI DMs | Zapify",
      description: "When followers comment on your posts or Reels, Zapify DMs them your link instantly using AI. No manual work, no missed opportunities.",
      keywords: "auto reply Instagram comments, comment to DM automation, Instagram comment automation, auto DM from comments, AI comment reply Instagram",
    },
    hero: {
      h1: "Comments become customers — on autopilot.",
      subtitle: "When followers comment on your posts or Reels, Zapify DMs them your link instantly. AI-powered replies feel personal, not robotic.",
    },
    phonePreview: {
      username: "sarah.styles",
      profilePic: "SS",
      messages: [
        { from: "user", text: "OMG where is this from?! I need it 😍" },
        { from: "bot", text: "Hey Sarah! Thanks for the love 🤍" },
        { from: "bot", text: "Here's the link you asked for 👇", hasLink: { label: "Shop the Look", url: "shopmy.bio/link" } },
        { from: "user", text: "Just ordered! Thank you!! 💕" },
      ],
    },
    problem: {
      headline: "Every unanswered comment is a missed chance to connect.",
      description: "When your posts start performing, comment threads fill up fast. Replying manually takes hours, and the longer you wait, the colder those leads get. Zapify helps you stay personal — without being glued to your phone.",
      painPoints: [
        { title: "Missed Replies", description: "Leads forget or lose interest when replies are delayed." },
        { title: "Manual Labor", description: "Copy-pasting the same response across dozens of posts." },
        { title: "Lost Sales", description: "Potential buyers never get your link or next step." },
      ],
    },
    steps: {
      headline: "Set up once. Keep responding.",
      items: [
        { title: "Connect Instagram", description: "Safe setup via Meta's official Graph API." },
        { title: "Set Trigger Keywords", description: "Choose words like \"LINK\", \"INFO\", or \"BUY\"." },
        { title: "Watch Comments Convert", description: "Every comment becomes an opportunity." },
      ],
    },
    flows: {
      headline: "Turn post engagement into instant conversations.",
      subtitle: "Here's how creators and brands use auto comment replies to save hours and close more sales directly in DMs.",
      items: [
        { icon: "Video", title: "Reel Comment", trigger: "\"LINK\"", response: "\"Here's the product link 👇\"" },
        { icon: "Image", title: "Post Comment", trigger: "\"Want this!\"", response: "\"Check your DMs! 🎉\"" },
        { icon: "Gift", title: "Contest Post", trigger: "\"ENTER\"", response: "\"You're entered! Good luck 🍀\"" },
        { icon: "Tag", title: "Promo Post", trigger: "\"CODE\"", response: "\"Here's your exclusive discount 👇\"" },
      ],
    },
    faqs: [
      { question: "Can I apply this to specific posts?", answer: "Yes, you can apply automations to one post, all new posts, or even your next upcoming post." },
      { question: "Does it work with Reels?", answer: "Absolutely — Reels comments are one of the top-performing triggers. Many creators use this to drive massive engagement." },
      { question: "Can I send affiliate links?", answer: "Yes — Zapify works with any link: LTK, Shopify, Amazon, or your own website." },
      { question: "How many DMs can I send per hour?", answer: "Instagram allows up to 200 DMs per hour (per Meta API rules). Zapify respects this limit automatically." },
      { question: "Does it work with carousel posts?", answer: "Yes — any post type that allows comments can trigger DMs, including carousels, single images, and Reels." },
      { question: "How fast are the replies?", answer: "DMs are sent within seconds of detecting a matching comment. Zapify uses Meta's official Graph API for real-time webhook notifications." },
    ],
    relatedSlugs: ["instagram-dm-link-automation", "instagram-story-reply-automation", "instagram-dm-email-collection"],
    overview: {
      headline: "What Instagram Comment-to-DM Automation Actually Does",
      body: "Instagram comment-to-DM automation turns every comment on your posts and Reels into an instant, personalized direct message. Instead of manually replying to hundreds of comments with the same link, Zapify detects trigger keywords — like \"LINK,\" \"INFO,\" or \"BUY\" — and sends a DM within seconds. This is how top creators monetize their content without spending hours in their inbox. Whether you sell physical products, digital courses, coaching sessions, or affiliate offers, comment automation bridges the gap between engagement and revenue. It works across single posts, carousels, Reels, and even your next upcoming post. The Instagram algorithm rewards posts that generate DMs, which means automated replies actually boost your organic reach. Zapify uses Meta's official Graph API — zero ban risk, full compliance, and enterprise-grade reliability. Set it up once, and every future comment triggers the right response at the right time.",
      highlights: [
        { icon: "Zap", title: "Instant DM Replies", text: "Every matching comment triggers a personalized DM within seconds — while the follower is still engaged." },
        { icon: "Target", title: "Keyword Triggers", text: "Set keywords like LINK, BUY, or INFO. Zapify only responds when they match — no spam, just relevance." },
        { icon: "TrendingUp", title: "Algorithm Boost", text: "Posts that generate DMs get more reach. Automated replies actually improve your organic distribution." },
        { icon: "Shield", title: "Meta-Approved & Safe", text: "Built on Meta's official Graph API. Zero ban risk, fully compliant, enterprise-grade reliability." },
      ],
      assetSlug: "automate-response",
      hasGif: true,
    },
    proofPoints: {
      headline: "The Numbers Behind Comment Automation",
      items: [
        { metric: "500%", label: "Faster Replies", description: "Instant DMs vs. hours of manual responses. Speed closes deals." },
        { metric: "3-5x", label: "More Conversions", description: "Leads who get instant responses convert at 3-5x higher rates." },
        { metric: "20+", label: "Hours Saved/Month", description: "Stop copy-pasting. Reclaim your time for content creation." },
        { metric: "24/7", label: "Always On", description: "Replies go out at 2 AM, on weekends, during vacation — you never miss a lead." },
      ],
    },
    workflowGuide: {
      headline: "How to Make Comment Automation Part of Your Workflow",
      subtitle: "From setup to your first automated DM in under 5 minutes.",
      steps: [
        { title: "Connect your Instagram Business Account", body: "Link your account through Meta's official OAuth flow. No passwords shared, no third-party access tokens. Zapify is a verified Meta Business Partner, so your account stays safe and fully compliant with Instagram's terms of service." },
        { title: "Choose your posts and set trigger keywords", body: "Select specific posts, enable \"Any Post\" mode, or set up \"Next Post\" to auto-activate on your next upload. Then define the keywords that trigger a DM — common ones include LINK, INFO, PRICE, BUY, and GUIDE. You can use multiple keywords per automation and customize the response for each." },
        { title: "Write your DM response and go live", body: "Craft a natural, conversational DM that includes your link, offer, or call-to-action. Zapify supports AI-powered personalization so each message feels human. Hit activate — and every matching comment from this point forward triggers an instant DM. Track performance in your Zapify dashboard in real time." },
      ],
    },
    testimonial: {
      quote: "I used to spend 2 hours every evening replying to comments with the same link. Now Zapify handles it in seconds. My conversion rate tripled in the first week.",
      name: "Sarah Chen",
      handle: "@sarah.styles",
      role: "Fashion Creator, 45K followers",
    },
  },

  // ─── 2. Send Links in DMs ────────────────────────────────
  {
    slug: "instagram-dm-link-automation",
    name: "Send Links in DMs",
    badge: "Link Delivery",
    description: "Stop copy-pasting links. Zapify sends your links the second someone asks — unlimited deliveries.",
    icon: "Link",
    color: {
      text: "text-purple-600 dark:text-purple-400",
      bg: "bg-purple-50 dark:bg-purple-950/40",
      border: "border-purple-200 dark:border-purple-800/50",
    },
    seo: {
      title: "Auto-Send Links in Instagram DMs with AI | Zapify",
      description: "Stop copy-pasting links. Zapify sends your product links, affiliate URLs, and freebies the second someone asks. One setup, unlimited deliveries.",
      keywords: "auto send links Instagram DM, Instagram link automation, send links in DMs, affiliate link automation Instagram, auto link delivery DM",
    },
    hero: {
      h1: "Stop copy-pasting. Start scaling.",
      subtitle: "Zapify sends your links the second someone asks — product pages, affiliate URLs, freebies. One setup, unlimited deliveries.",
    },
    phonePreview: {
      username: "mike.reviews",
      profilePic: "MR",
      messages: [
        { from: "user", text: "Hey! Where can I get that camera you recommended?" },
        { from: "bot", text: "Great taste Mike! 📸 Here's the exact camera I use:", hasLink: { label: "Canon R50 — Amazon", url: "amzn.to/3xK9..." } },
        { from: "user", text: "Perfect, thanks! Adding to cart now 🛒" },
      ],
    },
    problem: {
      headline: "Every link you send by hand costs time — and lost attention.",
      description: "Every post you share triggers the same flood of DMs: \"Where can I find this?\" Manually replying kills your focus and slows down conversions. Zapify sends the right link instantly.",
      painPoints: [
        { title: "Repetitive Work", description: "You send the same link 50 times per day." },
        { title: "Slow Replies", description: "By the time you respond, followers move on." },
        { title: "Missed Conversions", description: "Delays cost sales and attention." },
      ],
    },
    steps: {
      headline: "Set up once. Keep sharing.",
      items: [
        { title: "Connect Instagram", description: "Safe setup via Meta's official Graph API." },
        { title: "Add Your Links", description: "Products, courses, calendars — whatever you share." },
        { title: "Create Triggers", description: "Choose keywords and watch links get delivered." },
      ],
    },
    flows: {
      headline: "From free guides to affiliate sales — it works everywhere.",
      subtitle: "Creators use Zapify to send the right link at the right time — automatically.",
      items: [
        { icon: "ShoppingBag", title: "Product Link", trigger: "\"LINK\"", response: "\"Here's the product 👇 [Shop link]\"" },
        { icon: "Smartphone", title: "Story Reply", trigger: "Story reaction", response: "\"Thanks! Here's the link 👇\"" },
        { icon: "BookOpen", title: "DM Keyword: \"GUIDE\"", trigger: "\"GUIDE\"", response: "\"Here's your free guide 👇\"" },
        { icon: "Tag", title: "Affiliate Link: \"CODE\"", trigger: "\"CODE\"", response: "\"Use my code for 15% off 👇\"" },
      ],
    },
    faqs: [
      { question: "Can I send different links per trigger?", answer: "Yes — each automation can deliver unique links based on different keywords or triggers." },
      { question: "Does it work with affiliate links?", answer: "Absolutely — it works with Amazon, LTK, Mavely, or any URL you want to share." },
      { question: "Can I track link clicks?", answer: "Yes — view engagement metrics right inside your Zapify dashboard." },
      { question: "Can I collect email before sending?", answer: "Yes — ask for an email first, then deliver the link automatically." },
      { question: "Is this safe for my account?", answer: "100%. Zapify is a Meta-approved Business Partner following all Instagram policies." },
    ],
    relatedSlugs: ["instagram-comment-dm-automation", "instagram-dm-email-collection", "instagram-story-reply-automation"],
    overview: {
      headline: "What Instagram DM Link Delivery Actually Does",
      body: "Instagram DM link delivery automates the most repetitive task creators face: sending the same link to dozens — or hundreds — of followers every single day. When someone comments a keyword like \"LINK\" on your post, DMs you directly, or replies to your Story, Zapify detects the trigger and delivers your product link, affiliate URL, or lead magnet within seconds. No manual copy-pasting, no delayed replies, no missed sales. This is how affiliate marketers earn commissions at scale, how course creators fill enrollment pages, and how coaches book discovery calls — all from Instagram DMs. Zapify works with any URL: Amazon, Shopify, Gumroad, LTK, Calendly, your own domain — anything you can paste as a link. Every delivered link is tracked so you can measure exactly which triggers and posts drive the most clicks. The best part? Your followers experience a fast, personal, and helpful interaction — not a generic bot response.",
      highlights: [
        { icon: "Link", title: "Any Link, Any Platform", text: "Amazon, Shopify, Gumroad, LTK, Calendly — if you can paste it, Zapify can deliver it." },
        { icon: "Clock", title: "Zero-Delay Delivery", text: "Links arrive in seconds, not hours. Followers get what they asked for while they're still interested." },
        { icon: "BarChart3", title: "Built-In Analytics", text: "Track every DM sent and link clicked. Know exactly which posts and keywords drive the most conversions." },
        { icon: "Repeat", title: "Unlimited Deliveries", text: "Set it up once. Every future comment, DM, or Story reply triggers the right link automatically." },
      ],
      assetSlug: "link-clicks",
      hasGif: true,
    },
    proofPoints: {
      headline: "Why Automated Link Delivery Converts",
      items: [
        { metric: "10x", label: "Faster Delivery", description: "Links arrive in seconds, not hours. Instant = more clicks." },
        { metric: "90%", label: "DM Open Rate", description: "DMs crush email open rates. Your link actually gets seen." },
        { metric: "0", label: "Links Missed", description: "Every single request gets a response. No more lost in the inbox." },
        { metric: "1,000+", label: "Free DMs/Month", description: "Start with Zapify's free tier. Scale when you're ready." },
      ],
    },
    workflowGuide: {
      headline: "How to Make Link Delivery Part of Your Workflow",
      subtitle: "One setup powers unlimited link deliveries — across all your posts.",
      steps: [
        { title: "Connect Instagram and add your links", body: "Link your Instagram Business account through Meta's official flow. Then add the URLs you want to deliver — product pages, affiliate links, course enrollment, lead magnets, or anything else. You can add as many links as you need and map each one to a different trigger." },
        { title: "Set keywords and automation triggers", body: "Define what triggers each link delivery: comment keywords (LINK, BUY, GUIDE), Story replies, or DM keywords. Zapify matches incoming messages against your triggers and fires the right response instantly. You can run multiple automations simultaneously across different posts." },
        { title: "Activate and track performance", body: "Go live and watch your links get delivered automatically. Every DM sent, link clicked, and email collected is tracked in your Zapify dashboard. Use the data to optimize your captions, refine your keywords, and double down on what converts. Your followers get instant gratification, and you get a hands-free revenue engine." },
      ],
    },
    testimonial: {
      quote: "My affiliate commissions jumped 40% in the first month. Followers actually get the link before they lose interest. Zapify paid for itself on day one.",
      name: "Mike Rodriguez",
      handle: "@mike.reviews",
      role: "Tech Reviewer, 28K followers",
    },
  },

  // ─── 3. Collect Emails ───────────────────────────────────
  {
    slug: "instagram-dm-email-collection",
    name: "Collect Emails",
    badge: "Email Collection",
    description: "Build your email list from Instagram DMs. Capture emails before delivering your freebie.",
    icon: "Mail",
    color: {
      text: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-50 dark:bg-emerald-950/40",
      border: "border-emerald-200 dark:border-emerald-800/50",
    },
    seo: {
      title: "Collect Emails from Instagram DMs Automatically | Zapify",
      description: "Build your email list from Instagram DMs. Zapify captures emails before delivering your freebie — so followers become subscribers you own.",
      keywords: "collect emails Instagram, Instagram email collection, DM email capture, build email list Instagram, lead generation Instagram DM",
    },
    hero: {
      h1: "Build your list from Instagram DMs.",
      subtitle: "Zapify captures emails before delivering your freebie — so followers become subscribers you own, not just followers you rent.",
    },
    phonePreview: {
      username: "jess.fitness",
      profilePic: "JF",
      messages: [
        { from: "user", text: "I want your free meal plan! 🥗" },
        { from: "bot", text: "You'll love it Jess! 🎉 Drop your email and I'll send it right over:" },
        { from: "user", text: "jess@gmail.com" },
        { from: "bot", text: "Sent! Check your inbox 📩", hasLink: { label: "Download Meal Plan", url: "zapify.pro/dl/meal..." } },
      ],
    },
    problem: {
      headline: "Followers disappear. Email subscribers stay.",
      description: "Instagram reach changes daily. If you're not capturing emails, you're building on rented land. But collecting emails manually through DMs is slow and messy. Zapify automates it.",
      painPoints: [
        { title: "Missed Leads", description: "Thousands of followers, zero subscribers." },
        { title: "Manual Follow-Up", description: "Copy-pasting forms or links into every chat." },
        { title: "Lost Opportunities", description: "No system to nurture leads or sell later." },
      ],
    },
    steps: {
      headline: "Set up once. Keep collecting.",
      items: [
        { title: "Connect Instagram", description: "Safe setup via Meta's official Graph API." },
        { title: "Create Email Gate", description: "Ask for email before sending your resource." },
        { title: "Export & Nurture", description: "Download emails and add to your CRM." },
      ],
    },
    flows: {
      headline: "Grow your email list without a single landing page.",
      subtitle: "Here's how creators and businesses use Zapify to collect more emails from real, engaged followers.",
      items: [
        { icon: "MessageCircle", title: "Comment-to-DM", trigger: "\"EBOOK\" comment", response: "\"Drop your email for the free guide 👇\"" },
        { icon: "Smartphone", title: "Story Reply", trigger: "Story reaction", response: "\"Want the link? Drop your email first 👇\"" },
        { icon: "Key", title: "Keyword DM: \"EBOOK\"", trigger: "\"EBOOK\"", response: "\"Email me at... → Here's your download 📚\"" },
        { icon: "UserPlus", title: "Welcome Flow", trigger: "New follower", response: "\"Welcome! Drop your email for a free gift 🎁\"" },
      ],
    },
    faqs: [
      { question: "How does email collection work?", answer: "Set a trigger (comment keyword, story reply, etc.). When someone triggers it, Zapify asks for their email, saves it, and sends your link automatically." },
      { question: "Is it GDPR compliant?", answer: "Yes — followers opt in voluntarily by providing their email. Data is stored securely and can be deleted on request." },
      { question: "Can I deliver a lead magnet after email?", answer: "Absolutely — upload your link, and it's delivered as soon as they confirm their email." },
      { question: "Can I export emails?", answer: "Yes — export your collected emails as CSV and import into any email platform (ConvertKit, Mailchimp, MailerLite, etc.)." },
      { question: "Can I segment by trigger?", answer: "Yes — you can label contacts by trigger (e.g., \"Ebook\", \"Discount\", \"Course\") to segment your audience." },
    ],
    relatedSlugs: ["instagram-comment-dm-automation", "instagram-dm-link-automation", "instagram-story-reply-automation"],
    overview: {
      headline: "What Instagram DM Email Collection Actually Does",
      body: "Instagram DM email collection turns your most engaged followers into email subscribers you actually own. Instead of hoping followers see your next post, you build a direct line of communication that no algorithm change can take away. Here's how it works: a follower comments a keyword like \"EBOOK\" or replies to your Story. Zapify sends a DM asking for their email address. The follower types their email, Zapify validates and stores it, then instantly delivers your lead magnet — a PDF, a discount code, a course link, whatever you promised. The entire exchange happens inside Instagram DMs, where open rates are 90%+ and the conversation feels natural. No landing pages, no complicated funnels, no friction. Creators using Zapify's email collection see 3-5x higher opt-in rates compared to traditional link-in-bio forms because the ask happens in a conversation, not on a cold web page. Every email is exportable to ConvertKit, Mailchimp, MailerLite, or any CRM you use.",
      highlights: [
        { icon: "Mail", title: "Conversational Capture", text: "Emails are collected inside a natural DM conversation — 3-5x higher opt-in rates than landing page forms." },
        { icon: "Lock", title: "Own Your Audience", text: "Followers disappear when algorithms change. Email subscribers are yours forever — no middleman." },
        { icon: "Zap", title: "Instant Lead Magnet Delivery", text: "The moment a follower shares their email, Zapify delivers your PDF, discount, or course link." },
        { icon: "Users", title: "Export to Any CRM", text: "Download your emails as CSV. Import into ConvertKit, Mailchimp, MailerLite, or any platform you use." },
      ],
      assetSlug: "collect-emails",
      hasGif: false,
    },
    proofPoints: {
      headline: "Why DM Email Collection Outperforms Everything Else",
      items: [
        { metric: "3-5x", label: "Higher Opt-In Rates", description: "Conversational email capture beats landing page forms every time." },
        { metric: "90%+", label: "DM Open Rate", description: "Your email ask actually gets read — unlike buried link-in-bio CTAs." },
        { metric: "100%", label: "Ownership", description: "Email lists don't get throttled by algorithms. You own the relationship." },
        { metric: "0", label: "Landing Pages Needed", description: "Collect emails directly in DMs. No website, no funnel builder, no friction." },
      ],
    },
    workflowGuide: {
      headline: "How to Make Email Collection Part of Your Workflow",
      subtitle: "Turn every Instagram interaction into a subscriber — automatically.",
      steps: [
        { title: "Create your lead magnet and email gate", body: "Prepare the resource you'll deliver (PDF, video, discount code, course access) and upload the link to Zapify. Then configure the email gate — this tells Zapify to ask for an email before delivering the resource. You can customize the ask message to match your brand voice." },
        { title: "Set triggers across posts, Stories, and DMs", body: "Choose how followers opt in: comment keywords (\"EBOOK\", \"FREE\", \"GUIDE\"), Story replies, or DM keywords. When a follower triggers the automation, Zapify asks for their email in a friendly, conversational DM. Once they reply with a valid email, the resource is delivered instantly." },
        { title: "Export emails and nurture your list", body: "Every collected email appears in your Zapify dashboard, tagged by the trigger that captured it. Export as CSV and import into ConvertKit, Mailchimp, MailerLite, or any email platform. Now you have a segmented list of warm leads who already engaged with your content — the highest-quality subscribers you can get." },
      ],
    },
    testimonial: {
      quote: "I collected 1,200 emails in my first month without a single landing page. My newsletter list grew more from Instagram DMs than from two years of link-in-bio forms.",
      name: "Jess Martinez",
      handle: "@jess.fitness",
      role: "Fitness Creator, 62K followers",
    },
  },

  // ─── 4. Story Replies ────────────────────────────────────
  {
    slug: "instagram-story-reply-automation",
    name: "Story Replies",
    badge: "Story Automation",
    description: "Turn story reactions into real conversations. Respond with your link or offer automatically.",
    icon: "Smartphone",
    color: {
      text: "text-pink-600 dark:text-pink-400",
      bg: "bg-pink-50 dark:bg-pink-950/40",
      border: "border-pink-200 dark:border-pink-800/50",
    },
    seo: {
      title: "Auto-Reply to Instagram Story Reactions with AI DMs | Zapify",
      description: "Turn story reactions into real conversations. Zapify responds with your link or offer when followers react to your Stories. AI-powered and instant.",
      keywords: "Instagram story reply automation, auto reply story reactions, Instagram story DM automation, story reaction auto response, AI story reply",
    },
    hero: {
      h1: "Story reactions become real conversations.",
      subtitle: "When followers react to your Stories, Zapify responds with your link or offer. Turn that 🔥 emoji into actual engagement — automatically.",
    },
    phonePreview: {
      username: "alex.travels",
      profilePic: "AT",
      messages: [
        { from: "user", text: "🔥🔥🔥 Where is this?! I need to go!" },
        { from: "bot", text: "Bali! 🌴 So glad you love it Alex!" },
        { from: "bot", text: "Here's my full travel guide with all the spots:", hasLink: { label: "Bali Travel Guide", url: "zapify.pro/guide/ba..." } },
        { from: "user", text: "Booking flights rn ✈️ thank youuu!" },
      ],
    },
    problem: {
      headline: "Your Stories are working — but your DMs are overflowing.",
      description: "Story reactions are your most personal form of engagement. But they disappear fast if you don't reply. Manual follow-up is impossible when hundreds of people react daily. Zapify captures that attention automatically.",
      painPoints: [
        { title: "Missed Opportunities", description: "Every unread Story reply is a lost sale or lead." },
        { title: "No Time to Reply", description: "Reactions pile up while you're creating." },
        { title: "Cold Conversations", description: "Interest fades before you can respond." },
      ],
    },
    steps: {
      headline: "Set up once. Keep engaging.",
      items: [
        { title: "Connect Instagram", description: "Safe setup via Meta's official Graph API." },
        { title: "Create Story Trigger", description: "Choose what triggers an auto-reply." },
        { title: "Watch Engagement Convert", description: "Every Story reply becomes an opportunity." },
      ],
    },
    flows: {
      headline: "Keep Story engagement alive — automatically.",
      subtitle: "Here's how creators and brands use Zapify to make every Story reply count.",
      items: [
        { icon: "Flame", title: "Reaction: 🔥", trigger: "Fire emoji", response: "\"Thanks! Here's the product link 👇\"" },
        { icon: "MessageCircle", title: "Reply: \"Need this!\"", trigger: "\"Need this!\"", response: "\"Here's where to get it 👇\"" },
        { icon: "Tag", title: "DM: \"Discount?\"", trigger: "\"Discount?\"", response: "\"Here's your exclusive code 👇\"" },
        { icon: "BarChart3", title: "Poll Click: \"Yes!\"", trigger: "\"Yes!\"", response: "\"Here's your exclusive offer 👇\"" },
      ],
    },
    faqs: [
      { question: "Can any emoji trigger a DM?", answer: "Yes — any emoji or text reply to your Story can trigger an instant automated DM." },
      { question: "Can I send links from story replies?", answer: "Absolutely — deliver links, discounts, forms, or any content instantly." },
      { question: "Will it feel spammy?", answer: "No — messages are personalized and follow Instagram's approved DM format. They feel natural and conversational." },
      { question: "Can I set unique DMs per Story?", answer: "Yes — assign different automated responses per Story for targeted offers and messaging." },
      { question: "Is it safe?", answer: "Yes. Zapify uses Meta's official Graph API — no password sharing, no shadow tools, fully Meta-approved." },
      { question: "Can I filter by keywords?", answer: "Yes — only respond to specific words, phrases, or emojis. Ignore generic replies to keep automation targeted." },
    ],
    relatedSlugs: ["instagram-comment-dm-automation", "instagram-dm-link-automation", "instagram-dm-email-collection"],
    overview: {
      headline: "What Instagram Story Reply Automation Actually Does",
      body: "Instagram Story reply automation captures the most fleeting and personal form of engagement on the platform: Story reactions. When a follower sends a 🔥, ❤️, or a quick reply to your Story, they're expressing peak interest — but that interest evaporates fast. If you don't respond within minutes, the conversation dies. Zapify solves this by detecting Story reactions and replies in real time and sending an instant, personalized DM. Whether it's a product link, a discount code, a booking page, or a freebie, the right offer reaches the right person at the exact moment they're most engaged. Story automation is especially powerful because Stories are ephemeral — they disappear in 24 hours, but the DM conversation they spark can last forever. Creators using Story reply automation see dramatic increases in DM conversations, link clicks, and conversions because the response arrives while the follower is still watching your content. Zero delay, zero effort, maximum impact.",
      highlights: [
        { icon: "Flame", title: "Capture Peak Interest", text: "Story reactions signal maximum intent. Zapify replies while that interest is still hot — not hours later." },
        { icon: "MessageCircle", title: "Reactions to Real Conversations", text: "A fire emoji becomes a DM with your link, offer, or booking page. Every reaction is an opportunity." },
        { icon: "Clock", title: "Sub-3-Second Response", text: "Replies arrive while the follower is still watching your Story. Context + speed = maximum conversions." },
        { icon: "Smartphone", title: "Per-Story Customization", text: "Different Stories can trigger different responses. Product Story sends a shop link, Q&A sends a booking page." },
      ],
      assetSlug: "dm-automation",
      hasGif: false,
    },
    proofPoints: {
      headline: "Why Story Reply Automation Drives Results",
      items: [
        { metric: "400%", label: "More DM Conversations", description: "Instant replies turn silent reactions into real conversations." },
        { metric: "<3s", label: "Response Time", description: "Replies arrive while the follower is still watching your Story." },
        { metric: "2x", label: "Link Click Rate", description: "Context-aware DMs get double the clicks of cold outreach." },
        { metric: "24/7", label: "Always Responding", description: "Stories go up anytime — Zapify replies anytime too." },
      ],
    },
    workflowGuide: {
      headline: "How to Make Story Automation Part of Your Workflow",
      subtitle: "Turn every Story reaction into a conversion — no manual replies needed.",
      steps: [
        { title: "Connect Instagram and create a Story trigger", body: "Link your Instagram Business account and set up a Story automation. Choose whether to trigger on any Story reply, specific emoji reactions, or keyword-based responses. You can create different automations for different Stories — so a product Story sends a shop link while a Q&A Story sends a booking page." },
        { title: "Write your automated DM response", body: "Craft a natural, on-brand DM that delivers value immediately. Include your link, offer, or call-to-action. Zapify supports AI-powered personalization so the message adapts to the follower's name and the context of their reply. The goal is a response that feels like you typed it yourself — just faster." },
        { title: "Post Stories and let Zapify handle the rest", body: "Upload your Story as usual. Every reaction and reply triggers your automation instantly. Track how many DMs were sent, links clicked, and emails collected per Story in your Zapify dashboard. Use the data to learn which Story formats and CTAs drive the most conversions, then double down on what works." },
      ],
    },
    testimonial: {
      quote: "My Story engagement was always high, but I could never reply fast enough. Zapify turned my Stories into a real sales channel — I made $2K in affiliate commissions last month just from Story automations.",
      name: "Alex Rivera",
      handle: "@alex.travels",
      role: "Travel Creator, 38K followers",
    },
  },
]

export function getUseCaseBySlug(slug: string): UseCaseData | undefined {
  return useCases.find((uc) => uc.slug === slug)
}

export function getRelatedUseCases(slug: string): UseCaseData[] {
  const useCase = getUseCaseBySlug(slug)
  if (!useCase) return []
  return useCase.relatedSlugs
    .map((s) => getUseCaseBySlug(s))
    .filter((uc): uc is UseCaseData => uc !== undefined)
}
